import React from 'react';
import { Link } from 'react-router-dom';
import { QuizApiClient } from '@/infrastructure/quiz/QuizApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { getGuestAttemptId } from '@/utils/guestAttemptId';
import { enrichQuizHistoryRows } from '@/utils/quizSubmitDetailCache';
import QuizResultQuestionBreakdown from '@/presentation/quiz/QuizResultQuestionBreakdown';
import { getLocaleDateFormatTag } from '@/i18n/localeCatalog';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

function fmtDuration(sec, t) {
  const n = Math.max(0, Number(sec || 0));
  const m = Math.floor(n / 60);
  const s = n % 60;
  return t('common.durationFmt', { m: String(m), s: String(s).padStart(2, '0') });
}

function fmtDate(ts, locale) {
  if (!ts) return 'N/A';
  try {
    const tag = getLocaleDateFormatTag(locale);
    return new Date(Number(ts || 0)).toLocaleString(tag);
  } catch {
    return 'N/A';
  }
}

function passLabel(row) {
  if (row.passed != null) return row.passed;
  const ps = Number(row.pass_score ?? 70);
  return Number(row.score || 0) >= ps;
}

export default function QuizHistoryDetailPage({ session, resultId }) {
  const { t, locale } = useAppTranslation();
  const api = React.useMemo(() => new QuizApiClient(), []);
  const token = session?.accessToken || null;
  const guestId = !token ? getGuestAttemptId() : '';
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [row, setRow] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    const req = token ? api.getMyQuizResults(token) : guestId ? api.getGuestQuizResults(guestId) : Promise.resolve({ items: [] });
    req
      .then((data) => {
        if (!mounted) return;
        const enriched = enrichQuizHistoryRows(Array.isArray(data?.items) ? data.items : []);
        const found = enriched.find((x) => String(x.id) === String(resultId)) || null;
        setRow(found);
      })
      .catch((e) => {
        if (!mounted) return;
        const raw = String(e?.message || e || '').trim();
        setError(formatApiErrorMessage(raw, t) || t('errors.loadHistoryDetail'));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [api, token, guestId, resultId, t]);

  const questionResults = Array.isArray(row?.question_results) ? row.question_results : [];
  const passed = row ? passLabel(row) : false;

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-headline text-3xl font-extrabold">{t('history.detailPageTitle')}</h1>
        <Link
          to="/history"
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#004ac6] hover:text-[#004ac6]"
        >
          <span className="material-symbols-outlined !text-base">arrow_back</span>
          {t('history.backToList')}
        </Link>
      </div>

      {loading ? <p className="text-sm text-slate-600">{t('history.loadingDetail')}</p> : null}
      {error ? <p className="text-sm font-semibold text-rose-700">{error}</p> : null}
      {!loading && !error && !row ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
          {t('history.notFoundInCurrent')}
        </div>
      ) : null}

      {row ? (
        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-bold text-slate-900">
              {row.quiz_title || t('history.quizTitleFallback', { id: row.quiz_id })}
            </h2>
            <span className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-bold text-cyan-700">
              {row.score}% ({row.correct_answers}/{row.total_questions})
            </span>
          </div>
          <div className="mt-2 grid gap-2 text-sm text-slate-600 md:grid-cols-3">
            <p>{t('history.at', { time: fmtDate(row.timestamp, locale) })}</p>
            <p>{t('history.duration', { value: fmtDuration(row.time_taken, t) })}</p>
            <p>{t('history.passMark', { pct: Number(row.pass_score || 70) })}</p>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-[#dbe1ff] p-4">
              <p className="text-xs font-bold uppercase text-[#003ea8]">{t('history.scoreStat')}</p>
              <p className="mt-1 text-2xl font-extrabold text-[#004ac6] md:text-3xl">{row.score}%</p>
            </div>
            <div className="rounded-xl bg-[#6cf8bb]/30 p-4">
              <p className="text-xs font-bold uppercase text-[#005236]">{t('history.correctStat')}</p>
              <p className="mt-1 text-2xl font-extrabold text-[#006c49] md:text-3xl">
                {row.correct_answers}/{row.total_questions}
              </p>
            </div>
            <div className="rounded-xl bg-[#ffecea] p-4">
              <p className="text-xs font-bold uppercase text-[#930013]">{t('history.statusStat')}</p>
              <p className="mt-1 text-xl font-extrabold text-[#ab0b1c] md:text-2xl">
                {passed ? t('history.passedBadge') : t('history.notPassedBadge')}
              </p>
            </div>
          </div>

          {questionResults.length > 0 ? (
            <div className="mt-5">
              <QuizResultQuestionBreakdown questionResults={questionResults} />
            </div>
          ) : (
            <p className="mt-5 rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
              {t('history.noQuestionBreakdown')}
            </p>
          )}
        </section>
      ) : null}
    </div>
  );
}
